/**
 * useFederatedModule — dynamischer Federation-Import mit self-healing Retry.
 *
 * Ersetzt statische `import { X } from 'bauberichteV2/public'`-Stellen im
 * Portal-Code. Statische Federation-Imports zwingen den @module-federation/vite-
 * Runtime, ihre Preload-Chain fuer alle Remotes ZWINGEND vor `main.tsx` zu
 * feuern — ein einziger down-Container kippt dann den Boot, der Login-Screen
 * erscheint nicht mal.
 *
 * Mit diesem Hook:
 *  1. Portal bootet unabhaengig von Federation-Container-Status.
 *  2. Consumer bekommt `null` solange das Modul nicht geladen ist.
 *  3. Wenn der Container spaeter zurueckkommt (Retry-Loop in registry.ts),
 *     feuert `federation-module-registered` und der Hook retriggert seinen
 *     eigenen `import()` — Modul erscheint ohne Reload im UI.
 *
 * Verwendung:
 * ```tsx
 * const mod = useFederatedModule<typeof import('bauberichteV2/public')>('bauberichteV2/public');
 * if (!mod) return null; // oder Fallback-UI
 * return <mod.TaskEditModal ... />;
 * ```
 *
 * Alle Aufrufe fuer denselben Import-Path teilen sich einen Modul-Cache —
 * kein doppelter Netzwerk-Fetch.
 */
import { useEffect, useState } from 'react';
import { loadWithAsyncInitRetry } from '../modules/registry';

// Cache pro Import-Path — verhindert doppelte Fetches wenn mehrere Consumer
// dasselbe Modul brauchen. Wert ist entweder das geladene Modul oder eine
// laufende Promise (deduplication).
const moduleCache = new Map<string, unknown>();
const pendingCache = new Map<string, Promise<unknown>>();

// Import-Loader Map — dynamische Imports MUESSEN in Vite als static-analyzable
// string-literal geschrieben sein, sonst kann der Federation-Runtime sie nicht
// aufloesen. Deshalb hier alle bekannten Paths einmal hardcoded als Funktion.
// Neu geadde Federation-Paths hier ergaenzen.
const LOADERS: Record<string, () => Promise<unknown>> = {
  // bauberichteV2
  'bauberichteV2/public': () => import('bauberichteV2/public'),
  // bibliothek
  'bibliothek/public': () => import('bibliothek/public'),
  // stammdaten
  'stammdaten/public': () => import('stammdaten/public'),
  // onvi-capture (Konsument: bauberichteV2 Journal-Seiten via Host-Bruecke)
  'onvi-capture/public': () => import('onvi-capture/public'),
  // zeiterfassung
  'zeiterfassung/public': () => import('zeiterfassung/public'),
  // plantafel
  'plantafel/public': () => import('plantafel/public'),
};

/**
 * Modul dynamisch laden. Returned Promise haltet das Ergebnis im Cache.
 * Bei Fehler wird der Cache-Eintrag geloescht — beim naechsten Aufruf wird
 * neu probiert (self-healing).
 *
 * Exportiert seit 2026-08-21 (FED-01): main.tsx registriert diese Funktion
 * als `window.__onviceHostFederation.load` — die Host-Federation-Bruecke,
 * ueber die Federation-REMOTES (zeiterfassung, plantafel) ihre Cross-Modul-
 * Imports laden, statt eigene remotes-Deklarationen + verschachtelte
 * MF-Runtime-Instanzen zu fahren (deren Interop klemmt deterministisch,
 * siehe Fehlerliste 2026-08-20-federation-rote-punkte-sticky).
 */
export async function loadFederatedModule(path: string): Promise<unknown> {
  if (moduleCache.has(path)) return moduleCache.get(path);
  if (pendingCache.has(path)) return pendingCache.get(path);

  const loader = LOADERS[path];
  if (!loader) {
    console.warn(`[useFederatedModule] Kein Loader fuer "${path}" registriert`);
    return null;
  }

  const p = (async () => {
    try {
      // Suspense-Treiber (FED-01): laeuft die Init des Ziel-Remotes noch,
      // rejected ein nackter import() mit der geworfenen Promise. Die
      // Schleife awaited + ruft neu, bis die Kette konvergiert (identisch
      // zum bewaehrten Manifest-Ladepfad in modules/registry.ts).
      const mod = await loadWithAsyncInitRetry(loader, 20_000);
      moduleCache.set(path, mod);
      return mod;
    } catch (err) {
      // Kein Cache-Eintrag — beim naechsten Retry wird neu probiert.
      console.warn(`[useFederatedModule] "${path}" nicht ladbar:`, (err as Error)?.message ?? err);
      return null;
    } finally {
      pendingCache.delete(path);
    }
  })();

  pendingCache.set(path, p);
  return p;
}

/**
 * React-Hook: laed ein Federation-Modul dynamisch, hoert auf Registry-Events
 * fuer Self-Healing.
 */
export function useFederatedModule<T = unknown>(path: string): T | null {
  const [mod, setMod] = useState<T | null>(() => (moduleCache.get(path) as T | undefined) ?? null);

  useEffect(() => {
    let cancelled = false;

    const tryLoad = () => {
      loadFederatedModule(path).then((m) => {
        if (cancelled) return;
        if (m) setMod(m as T);
      });
    };

    // Initial-Load falls noch nicht im Cache
    if (!moduleCache.has(path)) tryLoad();

    // Auf Registry-Retry-Erfolg horchen — dann Modul evtl. jetzt verfuegbar
    const onRegistered = (e: Event) => {
      const detail = (e as CustomEvent<{ slug?: string }>).detail;
      const slug = detail?.slug;
      // Wenn das registrierte Modul unser Path-Prefix ist, neu probieren
      if (slug && path.startsWith(slug)) tryLoad();
    };
    window.addEventListener('federation-module-registered', onRegistered);

    return () => {
      cancelled = true;
      window.removeEventListener('federation-module-registered', onRegistered);
    };
  }, [path]);

  return mod;
}

/**
 * Cache leeren — fuer Tests oder wenn nach Logout alles gepurged werden soll.
 */
export function clearFederatedModuleCache(): void {
  moduleCache.clear();
  pendingCache.clear();
}

/**
 * Alle Cache-Eintraege fuer einen bestimmten Modul-Slug purgen.
 *
 * Aufgerufen aus dem SSE-Handler (federation-status-stream.ts) sobald der
 * Backend-Watchdog `status: 'down'` fuer diesen Slug meldet. Sonst wuerde
 * ein Consumer die noch im Speicher liegende Komponente weiter rendern —
 * obwohl das dahinter liegende Modul offline ist. Nach dem Purge liefert
 * `useFederatedModule(...)` `null`, der Consumer zeigt seine Offline-UI.
 */
export function invalidateFederatedModulesForSlug(slug: string): void {
  const prefix = `${slug}/`;
  for (const path of Array.from(moduleCache.keys())) {
    if (path.startsWith(prefix)) moduleCache.delete(path);
  }
  for (const path of Array.from(pendingCache.keys())) {
    if (path.startsWith(prefix)) pendingCache.delete(path);
  }
}
